import posthog from 'posthog-js';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import {
  useDeleteAttachmentsBulkDelete,
  useDeleteAttachmentsById,
  usePostAttachmentsBulkUpload,
  usePostAttachmentsUpload,
} from '@/api/generated/attachments/attachments';
import { ANALYTICS_EVENTS } from '@/config/analytics/events';

import { useGetAttachmentAccessUrl } from './useAttachment';

export interface UploadedFile {
  file?: File; // Store original File object for retry functionality
  fileName: string;
  fileSize?: number;
  id: string;
  mimeType?: string;
  status: 'processing' | 'done' | 'failed';
}

interface UseFileUploadsOptions {
  onFilesChange?: (fileCount: number) => void;
}

interface UseFileUploadsReturn {
  accessUrlMutation: ReturnType<typeof useGetAttachmentAccessUrl>;
  attachmentIds: Array<string>;
  bulkDeleteMutation: ReturnType<typeof useDeleteAttachmentsBulkDelete>;
  bulkUploadMutation: ReturnType<typeof usePostAttachmentsBulkUpload>;
  clearAttachments: () => void;
  deleteMutation: ReturnType<typeof useDeleteAttachmentsById>;

  handleClearAll: () => Promise<void>;
  handleFileDelete: (fileId: string) => Promise<void>;
  handleFileRetry: (fileId: string) => Promise<void>;
  // Handlers
  handleFilesAdded: (files: Array<File>) => Promise<void>;

  handleFileView: (fileId: string) => Promise<void>;
  hasEverUploadedFiles: boolean;
  mergeApiAttachments: (
    apiAttachments: Array<{
      fileName: string;
      fileSize?: number;
      id: string;
      mimeType?: string;
    }>,
  ) => void;
  setAttachmentIds: React.Dispatch<React.SetStateAction<Array<string>>>;
  setShowFilesUploaded: (show: boolean) => void;

  // State management
  setUploadedFiles: React.Dispatch<React.SetStateAction<Array<UploadedFile>>>;
  showFilesUploaded: boolean;
  // State
  uploadedFiles: Array<UploadedFile>;
  // Mutations
  uploadMutation: ReturnType<typeof usePostAttachmentsUpload>;
}

export function useFileUploads(options: UseFileUploadsOptions = {}): UseFileUploadsReturn {
  const { onFilesChange } = options;

  const { t } = useTranslation();
  const [uploadedFiles, setUploadedFiles] = useState<Array<UploadedFile>>([]);
  const [hasEverUploadedFiles, setHasEverUploadedFiles] = useState(false);
  const [attachmentIds, setAttachmentIds] = useState<Array<string>>([]);
  const [showFilesUploaded, setShowFilesUploaded] = useState(false);

  const uploadMutation = usePostAttachmentsUpload();
  const bulkUploadMutation = usePostAttachmentsBulkUpload();
  const deleteMutation = useDeleteAttachmentsById();
  const bulkDeleteMutation = useDeleteAttachmentsBulkDelete();
  const accessUrlMutation = useGetAttachmentAccessUrl();

  // Notify parent of file count changes
  const notifyFilesChange = useCallback(
    (count: number) => {
      onFilesChange?.(count);
    },
    [onFilesChange],
  );

  const clearAttachments = useCallback(() => {
    setAttachmentIds([]);
    setUploadedFiles([]);
    setHasEverUploadedFiles(false);
    setShowFilesUploaded(false);
    notifyFilesChange(0);
  }, [notifyFilesChange]);

  const handleFilesAdded = useCallback(
    async (files: Array<File>) => {
      const timestamp = Date.now();
      const newFiles: Array<UploadedFile> = files.map((file, index) => ({
        file, // Store File object for potential retry
        fileName: file.name,
        id: `uploading-${timestamp}-${index}-${Math.random().toString(36).slice(2, 9)}`,
        status: 'processing' as const,
      }));

      setUploadedFiles((prev) => {
        const updated = [...prev, ...newFiles];
        notifyFilesChange(updated.length);
        return updated;
      });
      setHasEverUploadedFiles(true);
      setShowFilesUploaded(true);

      // Use bulk upload if more than 1 file, otherwise use single upload
      if (files.length > 1) {
        // Bulk upload
        try {
          const response = await bulkUploadMutation.mutateAsync({ data: { files } });
          const uploadedAttachments = response;

          // Store all attachment IDs
          const newAttachmentIds = uploadedAttachments.map((att) => att.id);
          setAttachmentIds((prev) => [...prev, ...newAttachmentIds]);

          // Update all files with successful upload data
          setUploadedFiles((prev) => {
            const updated = prev.map((f) => {
              const matchingIndex = newFiles.findIndex((nf) => nf.id === f.id);
              if (matchingIndex !== -1 && matchingIndex < uploadedAttachments.length) {
                const attachment = uploadedAttachments[matchingIndex];
                if (attachment) {
                  const { file: _, ...rest } = f;
                  return {
                    ...rest,
                    fileName: attachment.fileName,
                    fileSize: attachment.fileSize,
                    id: attachment.id,
                    mimeType: attachment.mimeType,
                    status: 'done' as const,
                  };
                }
              }
              return f;
            });
            notifyFilesChange(updated.length);
            return updated;
          });
        } catch (error) {
          console.error('Bulk upload failed:', error);
          // Update all files with failed status, keep File objects for retry
          setUploadedFiles((prev) => {
            const updated = prev.map((f) => {
              const matchingIndex = newFiles.findIndex((nf) => nf.id === f.id);
              if (matchingIndex !== -1 && matchingIndex < files.length) {
                const file = files[matchingIndex];
                if (file) {
                  return { ...f, file, status: 'failed' as const };
                }
              }
              return f;
            });
            notifyFilesChange(updated.length);
            return updated;
          });
          toast.error(t('profile.toast.uploadFilesFailed'));
        }
      } else {
        // Single upload
        const uploadPromises = files.map(async (file, index) => {
          try {
            const response = await uploadMutation.mutateAsync({ data: { file } });
            const attachmentId = response.id;

            // Store attachment ID
            setAttachmentIds((prev) => [...prev, attachmentId]);

            // Update file with successful upload data, remove File object (no longer needed)
            setUploadedFiles((prev) => {
              const updated = prev.map((f) => {
                if (f.id === newFiles[index]?.id) {
                  const { file: _, ...rest } = f;
                  return {
                    ...rest,
                    fileName: response.fileName,
                    fileSize: response.fileSize,
                    id: attachmentId,
                    mimeType: response.mimeType,
                    status: 'done' as const,
                  };
                }
                return f;
              });
              notifyFilesChange(updated.length);
              return updated;
            });
          } catch (error) {
            console.error('File upload failed:', error);
            // Update file with failed status, keep the File object for retry
            setUploadedFiles((prev) => {
              const updated = prev.map((f) =>
                f.id === newFiles[index]?.id ? { ...f, file, status: 'failed' as const } : f,
              );
              notifyFilesChange(updated.length);
              return updated;
            });
            toast.error(
              t('profile.toast.uploadFileFailed', {
                fileName: file.name,
              }),
            );
          }
        });

        await Promise.all(uploadPromises);
      }
    },
    [uploadMutation, bulkUploadMutation, notifyFilesChange, t],
  );

  const handleFileDelete = useCallback(
    async (fileId: string) => {
      try {
        await deleteMutation.mutateAsync({ id: fileId });
        setUploadedFiles((prev) => {
          const remaining = prev.filter((f) => f.id !== fileId);
          notifyFilesChange(remaining.length);
          // Close dropdown if no files remain, but keep tab visible
          if (remaining.length === 0) {
            setShowFilesUploaded(false);
          }
          return remaining;
        });
        setAttachmentIds((prevIds) => prevIds.filter((id) => id !== fileId));
      } catch (error) {
        console.error('Failed to delete attachment:', error);
        toast.error(t('profile.toast.deleteFileFailed'));
      }
    },
    [deleteMutation, notifyFilesChange, t],
  );

  const handleClearAll = useCallback(async () => {
    try {
      const fileIds = uploadedFiles.map((f) => f.id);
      if (fileIds.length > 0) {
        await bulkDeleteMutation.mutateAsync({ data: { ids: fileIds } });
      }
      setUploadedFiles([]);
      setAttachmentIds([]);
      notifyFilesChange(0);
      // Close dropdown after clearing all files, but keep tab visible
      setShowFilesUploaded(false);
    } catch (error) {
      console.error('Failed to bulk delete attachments:', error);
      toast.error(t('profile.toast.deleteFilesFailed'));
    }
  }, [uploadedFiles, bulkDeleteMutation, notifyFilesChange, t]);

  const handleFileView = useCallback(
    async (fileId: string) => {
      const file = uploadedFiles.find((f) => f.id === fileId);
      if (!file) {
        return;
      }

      const isPdf =
        file.fileName.toLowerCase().endsWith('.pdf') || file.mimeType === 'application/pdf';

      if (isPdf) {
        try {
          const sasUrl = await accessUrlMutation.mutateAsync({
            disposition: 'inline',
            id: fileId,
          });

          posthog.capture(ANALYTICS_EVENTS.ATTACHMENT_PREVIEW_OPENED, {
            attachmentId: fileId,
            fileSize: file.fileSize,
            mimeType: file.mimeType,
          });

          const dialogUrl =
            `${window.location.origin}/taskpane.html?page=pdf-viewer` +
            `&url=${encodeURIComponent(sasUrl)}` +
            `&fileName=${encodeURIComponent(file.fileName)}`;

          Office.context.ui.displayDialogAsync(
            dialogUrl,
            { displayInIframe: false, height: 80, width: 80 },
            (result) => {
              if (result.status === Office.AsyncResultStatus.Failed) {
                toast.error('Failed to open document viewer');
              }
            },
          );
        } catch {
          toast.error(t('profile.toast.previewFileFailed'));
        }
      } else {
        try {
          const sasUrl = await accessUrlMutation.mutateAsync({
            disposition: 'attachment',
            id: fileId,
          });

          posthog.capture(ANALYTICS_EVENTS.ATTACHMENT_DOWNLOAD_TRIGGERED, {
            attachmentId: fileId,
            fileSize: file.fileSize,
            mimeType: file.mimeType,
          });

          // The SAS URL has Content-Disposition: attachment baked in, so the
          // browser saves the file regardless of CORS or the <a download> attr.
          const link = document.createElement('a');
          link.href = sasUrl;
          link.rel = 'noopener noreferrer';
          document.body.append(link);
          link.click();
          link.remove();
        } catch {
          toast.error(t('profile.toast.openFileFailed'));
        }
      }
    },
    [accessUrlMutation, uploadedFiles, t],
  );

  const handleFileRetry = useCallback(
    async (fileId: string) => {
      const failedFile = uploadedFiles.find((f) => f.id === fileId);
      if (!failedFile?.file) {
        console.error('File not found or File object missing for retry');
        toast.error(t('profile.toast.retryFileMissing'));
        return;
      }

      // Update status to processing
      setUploadedFiles((prev) =>
        prev.map((f) => (f.id === fileId ? { ...f, status: 'processing' as const } : f)),
      );

      try {
        const response = await uploadMutation.mutateAsync({ data: { file: failedFile.file } });
        const attachmentId = response.id;

        setAttachmentIds((prev) => [...prev, attachmentId]);

        // Update file with successful upload data, remove File object
        setUploadedFiles((prev) => {
          const updated = prev.map((f) => {
            if (f.id === fileId) {
              const { file: _, ...rest } = f;
              return {
                ...rest,
                fileName: response.fileName,
                fileSize: response.fileSize,
                id: attachmentId,
                mimeType: response.mimeType,
                status: 'done' as const,
              };
            }
            return f;
          });
          notifyFilesChange(updated.length);
          return updated;
        });
      } catch (error) {
        console.error('Retry upload failed:', error);
        setUploadedFiles((prev) =>
          prev.map((f) => (f.id === fileId ? { ...f, status: 'failed' as const } : f)),
        );
        toast.error(
          t('profile.toast.retryUploadFailed', {
            fileName: failedFile.fileName,
          }),
        );
      }
    },
    [uploadedFiles, uploadMutation, notifyFilesChange, t],
  );

  const mergeApiAttachments = useCallback(
    (
      apiAttachments: Array<{
        fileName: string;
        fileSize?: number;
        id: string;
        mimeType?: string;
      }>,
    ) => {
      if (apiAttachments.length > 0) {
        const mappedAttachments: Array<UploadedFile> = apiAttachments.map((att) => {
          const result: UploadedFile = {
            fileName: att.fileName,
            id: att.id,
            status: 'done' as const,
          };
          if (att.fileSize !== undefined) {
            result.fileSize = att.fileSize;
          }
          if (att.mimeType !== undefined) {
            result.mimeType = att.mimeType;
          }
          return result;
        });

        // Merge: keep locally uploading files, update with API data for completed ones
        setUploadedFiles((prev) => {
          const inProgressUploads = prev.filter(
            (f) => f.status === 'processing' || f.status === 'failed',
          );
          const apiIds = new Set(mappedAttachments.map((f) => f.id));
          // Filter out any local "done" files that are now in API response
          const localDone = prev.filter((f) => f.status === 'done' && !apiIds.has(f.id));
          const merged = [...mappedAttachments, ...localDone, ...inProgressUploads];
          notifyFilesChange(merged.length);
          return merged;
        });

        setAttachmentIds((prev) => {
          const combined = new Set([...prev, ...mappedAttachments.map((f) => f.id)]);
          return Array.from(combined);
        });

        setHasEverUploadedFiles(true);
      }
    },
    [notifyFilesChange],
  );

  return {
    // State
    attachmentIds,
    hasEverUploadedFiles,
    setShowFilesUploaded,
    showFilesUploaded,
    uploadedFiles,

    // Mutations
    accessUrlMutation,
    bulkDeleteMutation,
    bulkUploadMutation,
    deleteMutation,
    uploadMutation,

    // Handlers
    handleClearAll,
    handleFileDelete,
    handleFileRetry,
    handleFilesAdded,
    handleFileView,

    // State management
    clearAttachments,
    mergeApiAttachments,
    setAttachmentIds,
    setUploadedFiles,
  };
}
